// ⛔ THIS FILE EXISTS IN TWO REPOSITORIES AND MUST STAY BYTE-IDENTICAL.
//
//   canonical : my-jarvis-dashboard-erez/src/components/voice/
//   vendored  : tailormind-erez-console/web/src/erez/voice/
//
// They were identical and nothing said so, so a one-line bug —
// `isNaN(Infinity)` is false, which printed a literal "Infinity:NaN" where a
// clip's length belongs — had to be found and fixed twice, on 2026-08-22,
// after Erez spotted it on his agents board.
//
// Edit EITHER, then run:  $JARVIS/.claude/scripts/sync-voice-player.sh
// Check for drift with:   $JARVIS/.claude/scripts/sync-voice-player.sh --check
//
// ⚠️ And REBUILD AND DEPLOY THE CONSOLE. It ships a built bundle, so a source
// fix alone changes nothing on erez.tailormind.co.
//
// (VoicePanel.tsx is NOT shared — it has genuinely diverged between the two
// surfaces. Only files meant to be identical carry this banner.)

/**
 * Module-level singleton Audio element.
 * Shared between all VoicePlayerInline instances AND the app-level
 * AutoplayManager, so "one message plays at a time" is true globally —
 * swapping src stops the previous, regardless of which surface kicked
 * it off.
 */

const audio = new Audio();

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((fn) => fn());
}

audio.addEventListener("play", notify);
audio.addEventListener("pause", notify);
audio.addEventListener("ended", notify);
audio.addEventListener("timeupdate", notify);
audio.addEventListener("loadedmetadata", notify);

export const audioManager = {
  /** Play a URL. If same URL, resume; if different, start from beginning. */
  play(src: string) {
    const fullSrc = audio.src;
    const isSame = fullSrc.endsWith(src) || fullSrc === src;
    if (isSame) {
      audio.play().catch(() => {});
    } else {
      audio.src = src;
      audio.play().catch(() => {});
    }
  },

  pause() {
    audio.pause();
  },

  toggle(src: string) {
    if (audioManager.isPlayingSrc(src)) {
      audio.pause();
    } else {
      audioManager.play(src);
    }
  },

  seek(time: number) {
    audio.currentTime = time;
  },

  setSpeed(rate: number) {
    audio.playbackRate = rate;
  },

  isPlaying(): boolean {
    return !audio.paused;
  },

  isPlayingSrc(src: string): boolean {
    return !audio.paused && (audio.src.endsWith(src) || audio.src === src);
  },

  getCurrentSrc(): string {
    return audio.src;
  },

  getCurrentTime(): number {
    return audio.currentTime;
  },

  getDuration(): number {
    return audio.duration || 0;
  },

  /** Subscribe to state changes. Returns unsubscribe function. */
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
